// src/components/ChecklistAnalysisSection.js

import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Button,
  Card,
  CardContent,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  CircularProgress,
  Alert,
  Chip,
  Stack,
  Divider,
  Paper,
  LinearProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  IconButton,
  Tooltip,
  Rating,
  Collapse,
  TextField
} from '@mui/material';
import {
  PlayArrow as PlayIcon,
  Refresh as RefreshIcon,
  CheckCircle as CheckIcon,
  Error as ErrorIcon,
  HourglassEmpty as PendingIcon,
  Assessment as AssessmentIcon,
  Delete as DeleteIcon,
  Add as AddIcon,
  PlaylistAdd as PlaylistAddIcon,
  Assignment as AssignmentIcon,
  Close as CloseIcon,
  Info as InfoIcon,
  ExpandMore as ExpandMoreIcon,
  ExpandLess as ExpandLessIcon,
  Edit as EditIcon,
  Save as SaveIcon,
  NoteAdd as NoteAddIcon
} from '@mui/icons-material';
import { getFunctions, httpsCallable } from 'firebase/functions';
import { collection, query, where, getDocs, orderBy, addDoc, serverTimestamp, onSnapshot, updateDoc, deleteDoc, doc } from 'firebase/firestore';
import { getAuth } from 'firebase/auth';
import { db } from '../firebase';
import { useUserData } from '../hooks/useUserData';
import * as amplitude from '@amplitude/analytics-browser';

const ChecklistAnalysisSection = ({ docId, isTranscribed }) => {
  const [checklists, setChecklists] = useState([]);
  const [selectedChecklistId, setSelectedChecklistId] = useState('');
  const [analyses, setAnalyses] = useState([]);
  const [loading, setLoading] = useState(false);
  const [checklistsLoading, setChecklistsLoading] = useState(true);
  const [runningAnalysis, setRunningAnalysis] = useState(false);
  const [detailsDialogOpen, setDetailsDialogOpen] = useState(false);
  const [selectedAnalysis, setSelectedAnalysis] = useState(null);
  const [contextExpanded, setContextExpanded] = useState(false);
  const [comment, setComment] = useState('');
  const [isEditingComment, setIsEditingComment] = useState(false);
  const [savingComment, setSavingComment] = useState(false);
  const [createDialogOpen, setCreateDialogOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [analysisToDelete, setAnalysisToDelete] = useState(null);
  const [deleting, setDeleting] = useState(false);

  const auth = getAuth();
  const { userData } = useUserData();
  const hasBusinessPlan = userData?.plan?.toLowerCase() === 'business';

  // Fetch checklists
  useEffect(() => {
    if (!hasBusinessPlan || !auth.currentUser) {
      setChecklistsLoading(false);
      return;
    }

    const fetchChecklists = async () => {
      try {
        const q = query(
          collection(db, 'checklists'),
          where('userId', '==', auth.currentUser.uid),
          orderBy('createdAt', 'desc')
        );
        const snap = await getDocs(q);
        setChecklists(snap.docs.map(doc => ({ id: doc.id, ...doc.data() })));
      } catch (error) {
        console.error('Error fetching checklists:', error);
      } finally {
        setChecklistsLoading(false);
      }
    };

    fetchChecklists();
  }, [hasBusinessPlan, auth.currentUser]);

  // Listen to analyses for this recording
  useEffect(() => {
    if (!auth.currentUser || !docId) return;

    const q = query(
      collection(db, 'analyses'),
      where('recordingId', '==', docId),
      where('userId', '==', auth.currentUser.uid)
    );

    const unsubscribe = onSnapshot(q, (snapshot) => {
      const analysesData = snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));
      // Sort by createdAt descending (newest first)
      analysesData.sort((a, b) => {
        const aTime = a.createdAt?.toMillis?.() || (a.createdAt ? new Date(a.createdAt).getTime() : 0);
        const bTime = b.createdAt?.toMillis?.() || (b.createdAt ? new Date(b.createdAt).getTime() : 0);
        return bTime - aTime; // Descending order
      });
      setAnalyses(analysesData);
    }, (error) => {
      console.error('Error listening to analyses:', error);
    });

    return () => unsubscribe();
  }, [docId, auth.currentUser]);

  // Keep selectedAnalysis in sync with analyses updates
  useEffect(() => {
    if (selectedAnalysis && analyses.length > 0) {
      const updated = analyses.find(a => a.id === selectedAnalysis.id);
      if (updated) {
        // Only update if meaningful fields changed to avoid potential loops/jitters
        // deep compare or just check key fields. For now, we trust firestore snapshot stability.
        // We only want to update if we are NOT editing, OR if we are just viewing.
        // If we are editing, we don't want to overwrite the user's current input if the DB updates in background (rare but possible).
        // But the user issue is "updated comment not seen".
        // Let's safe update:
        setSelectedAnalysis(updated);
      }
    }
  }, [analyses]);

  const handleRunAnalysis = async () => {
    if (!selectedChecklistId || !auth.currentUser) return;

    try {
      setRunningAnalysis(true);

      // Create analysis document
      const analysisData = {
        userId: auth.currentUser.uid,
        recordingId: docId,
        checklistId: selectedChecklistId,
        status: 'pending',
        createdAt: serverTimestamp()
      };

      await addDoc(collection(db, 'analyses'), analysisData);

      // Trigger analysis via callable function
      const functions = getFunctions();
      const triggerAnalysis = httpsCallable(functions, 'triggerChecklistAnalysis');
      await triggerAnalysis({ recordingId: docId });

      amplitude.track('Checklist Analysis Started', { docId, checklistId: selectedChecklistId });
      setSelectedChecklistId('');
      setCreateDialogOpen(false);
    } catch (error) {
      console.error('Error running analysis:', error);
      alert('Սխալ՝ վերլուծությունը սկսելիս: ' + error.message);
    } finally {
      setRunningAnalysis(false);
    }
  };

  const handleDeleteClick = (analysis) => {
    setAnalysisToDelete(analysis);
    setDeleteDialogOpen(true);
  };

  const confirmDelete = async () => {
    if (!analysisToDelete || !auth.currentUser) return;

    setDeleting(true);
    try {
      await deleteDoc(doc(db, 'analyses', analysisToDelete.id));
      amplitude.track('Checklist Analysis Deleted', { analysisId: analysisToDelete.id, status: analysisToDelete.status });
      setDeleteDialogOpen(false);
      setAnalysisToDelete(null);
      // If the deleted analysis was selected/open, close the details dialog
      if (selectedAnalysis?.id === analysisToDelete.id) {
        setDetailsDialogOpen(false);
        setSelectedAnalysis(null);
      }
    } catch (error) {
      console.error('Error deleting analysis:', error);
      alert('Սխալ՝ վերլուծությունը ջնջելիս: ' + error.message);
    } finally {
      setDeleting(false);
    }
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'completed':
        return <CheckIcon color="success" />;
      case 'failed':
        return <ErrorIcon color="error" />;
      case 'processing':
        return <CircularProgress size={20} />;
      case 'pending':
        return <PendingIcon color="warning" />;
      default:
        return <InfoIcon />;
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'completed':
        return 'success';
      case 'failed':
        return 'error';
      case 'processing':
        return 'warning';
      case 'pending':
        return 'warning';
      default:
        return 'default';
    }
  };

  const getStatusText = (status) => {
    switch (status) {
      case 'completed':
        return 'Ավարտված';
      case 'failed':
        return 'Չհաջողվեց';
      case 'processing':
        return 'Վերլուծվում է';
      case 'pending':
        return 'Սպասում է';
      default:
        return status;
    }
  };

  const formatDate = (timestamp) => {
    if (!timestamp) return 'Ամսաթիվ չկա';
    const date = timestamp.toDate ? timestamp.toDate() : new Date(timestamp);
    return date.toLocaleString('hy-AM', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const renderAnalysisResults = (analysis) => {
    if (!analysis.results || !analysis.checklistSnapshot) return null;

    const { results, checklistSnapshot } = analysis;
    const questions = checklistSnapshot.questions || [];

    return (
      <Stack spacing={1.5} sx={{ mt: 1 }}>
        {questions.map((question, index) => {
          const result = results[question.id];
          if (!result) return null;

          return (
            <Paper
              key={question.id}
              elevation={0}
              sx={{
                p: 1.5,
                border: '1px solid',
                borderColor: 'divider',
                borderRadius: 1.5,
                bgcolor: 'grey.50'
              }}
            >
              <Stack spacing={1}>
                <Stack direction="row" spacing={1} alignItems="flex-start" justifyContent="space-between">
                  <Box sx={{ flex: 1, minWidth: 0 }}>
                    <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 0.5 }}>
                      <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600 }}>
                        #{index + 1}
                      </Typography>
                      <Chip
                        label={question.type === 'yes_no' ? 'Այո/Ոչ' : question.type === 'yes_no_partially' ? 'Այո/Ոչ/Մասամբ' : '1-10'}
                        size="small"
                        variant="outlined"
                        sx={{ height: 20, fontSize: '0.65rem' }}
                      />
                    </Stack>
                    <Typography variant="body2" sx={{ fontWeight: 500, lineHeight: 1.4 }}>
                      {question.text}
                    </Typography>
                  </Box>
                </Stack>

                <Stack direction="row" spacing={1.5} alignItems="center" sx={{ mt: 0.5 }}>
                  {question.type === 'yes_no' ? (
                    <Chip
                      label={result.answer === 'yes' ? 'Այո' : result.answer === 'no' ? 'Ոչ' : 'Չի կարելի որոշել'}
                      color={result.answer === 'yes' ? 'success' : result.answer === 'no' ? 'error' : 'default'}
                      size="small"
                      sx={{ fontWeight: 600, height: 24 }}
                    />
                  ) : question.type === 'yes_no_partially' ? (
                    <Chip
                      label={result.answer === 'yes' ? 'Այո' : result.answer === 'no' ? 'Ոչ' : result.answer === 'partially' ? 'Մասամբ' : 'Չի կարելի որոշել'}
                      color={result.answer === 'yes' ? 'success' : result.answer === 'no' ? 'error' : result.answer === 'partially' ? 'warning' : 'default'}
                      size="small"
                      sx={{ fontWeight: 600, height: 24 }}
                    />
                  ) : result.answer === 'cannot_tell' ? (
                    <Chip
                      label="Անհնար է որոշել"
                      color="default"
                      size="small"
                      sx={{ fontWeight: 600, height: 24 }}
                    />
                  ) : (
                    <Stack direction="row" spacing={1} alignItems="center">
                      <Rating value={result.answer} max={10} readOnly size="small" sx={{ fontSize: '1rem' }} />
                      <Typography variant="body2" sx={{ fontWeight: 600, minWidth: '35px' }}>
                        {result.answer}/10
                      </Typography>
                    </Stack>
                  )}
                </Stack>

                {result.comment && (
                  <Box
                    sx={{
                      mt: 1,
                      pt: 1,
                      borderTop: '1px solid',
                      borderColor: 'divider'
                    }}
                  >
                    <Typography
                      variant="caption"
                      sx={{
                        whiteSpace: 'pre-wrap',
                        lineHeight: 1.5,
                        color: 'text.secondary',
                        display: 'block'
                      }}
                    >
                      {result.comment}
                    </Typography>
                  </Box>
                )}
              </Stack>
            </Paper>
          );
        })}
      </Stack>
    );
  };

  if (!hasBusinessPlan) {
    return (
      <Card elevation={3} sx={{ borderRadius: 3, mt: 2 }}>
        <CardContent sx={{ textAlign: 'center', py: 4 }}>
          <AssessmentIcon sx={{ fontSize: 56, color: 'text.secondary', mb: 2 }} />
          <Typography variant="h6" gutterBottom sx={{ fontWeight: 600 }}>
            QA Ստուգացուցակները հասանելի են Business պլանում
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Ստեղծեք հարցաշարեր և ավտոմատ կերպով ստուգեք ձեր զանգերի կենտրոնի օպերատորների աշխատանքի որակը։
          </Typography>
        </CardContent>
      </Card>
    );
  }

  if (!isTranscribed) {
    return (
      <Card elevation={3} sx={{ borderRadius: 3, mt: 2 }}>
        <CardContent sx={{ textAlign: 'center', py: 4 }}>
          <AssessmentIcon sx={{ fontSize: 56, color: 'text.secondary', mb: 2 }} />
          <Typography variant="h6" gutterBottom sx={{ fontWeight: 600 }}>
            Վերլուծությունը հասանելի է միայն վերծանված ձայնագրությունների համար
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Խնդրում ենք սպասել, մինչև ձայնագրությունը վերծանվի։
          </Typography>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card elevation={3} sx={{ borderRadius: 3, mt: 2 }}>
      <CardContent>
        <Stack spacing={3}>
          <Box>
            <Typography variant="h6" sx={{ fontWeight: 600, mb: 1 }}>
              AI Վերլուծություն
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Ընտրեք ստուգացուցակ և ավտոմատ կերպով վերլուծեք ձայնագրությունը
            </Typography>
          </Box>

          <Divider />

          {/* Existing Analyses - Show First */}
          {analyses.length > 0 && (
            <Box>
              <Typography variant="subtitle2" sx={{ mb: 2, fontWeight: 600 }}>
                Վերլուծությունների պատմություն ({analyses.length})
              </Typography>
              <Stack spacing={2}>
                {analyses.map((analysis) => {
                  // Find checklist name for pending analysis if snapshot not available
                  let checklistName = analysis.checklistSnapshot?.name || 'Անհայտ ստուգացուցակ';
                  if (!analysis.checklistSnapshot && analysis.checklistId) {
                    const foundChecklist = checklists.find(c => c.id === analysis.checklistId);
                    if (foundChecklist) {
                      checklistName = foundChecklist.name;
                    }
                  }

                  const isProcessing = analysis.status === 'processing' || analysis.status === 'pending';
                  const isCompleted = analysis.status === 'completed';
                  const questionsCount = analysis.checklistSnapshot?.questions?.length || 0;
                  const commentSnippet = analysis.comment ? (
                    analysis.comment.length > 50 ? analysis.comment.substring(0, 50) + '...' : analysis.comment
                  ) : null;


                  return (
                    <Card
                      key={analysis.id}
                      variant="outlined"
                      sx={{
                        border: '1px solid',
                        borderColor: isCompleted ? 'success.main' : 'divider',
                        borderWidth: isCompleted ? 1 : 1,
                        '&:hover': {
                          boxShadow: 2,
                          borderColor: isCompleted ? 'success.dark' : 'text.disabled',
                          transition: 'all 0.2s ease-in-out'
                        },
                        cursor: isCompleted ? 'pointer' : 'default',
                        position: 'relative'
                      }}
                      onClick={isCompleted ? () => {
                        setSelectedAnalysis(analysis);
                        setComment(analysis.comment || '');
                        setIsEditingComment(false);
                        setDetailsDialogOpen(true);
                      } : undefined}
                    >
                      <CardContent sx={{ pb: '16px !important' }}>
                        <Stack spacing={1}>
                          <Stack direction="row" justifyContent="space-between" alignItems="flex-start">
                            <Box sx={{ flex: 1, mr: 2 }}>
                              <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 0.5 }}>
                                <AssignmentIcon fontSize="small" color="action" />
                                <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                                  {checklistName}
                                </Typography>
                              </Stack>

                              <Typography variant="caption" color="text.secondary" display="block">
                                {formatDate(analysis.createdAt)}
                              </Typography>

                              {isCompleted && (
                                <Stack direction="row" spacing={2} sx={{ mt: 1 }}>
                                  <Typography variant="caption" color="text.secondary">
                                    {questionsCount} հարց
                                  </Typography>
                                  {commentSnippet && (
                                    <Typography variant="caption" color="text.secondary" sx={{ fontStyle: 'italic' }}>
                                      "{commentSnippet}"
                                    </Typography>
                                  )}
                                </Stack>
                              )}
                            </Box>

                            <Stack direction="row" spacing={1} alignItems="center">
                              <Chip
                                icon={getStatusIcon(analysis.status)}
                                label={getStatusText(analysis.status)}
                                color={getStatusColor(analysis.status)}
                                size="small"
                                variant={isProcessing ? "outlined" : "filled"}
                              />
                              <Tooltip title={isProcessing ? "Չեղարկել" : "Ջնջել"}>
                                <IconButton
                                  size="small"
                                  color="error"
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    handleDeleteClick(analysis);
                                  }}
                                >
                                  <DeleteIcon fontSize="small" />
                                </IconButton>
                              </Tooltip>
                            </Stack>
                          </Stack>

                          {isProcessing && (
                            <Box sx={{ mt: 1 }}>
                              <LinearProgress />
                              <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, display: 'block' }}>
                                Վերլուծությունը ընթացքի մեջ է...
                              </Typography>
                            </Box>
                          )}

                          {analysis.status === 'failed' && analysis.error && (
                            <Alert severity="error" sx={{ mt: 1, py: 0 }}>
                              <Typography variant="caption">
                                {analysis.error}
                              </Typography>
                            </Alert>
                          )}
                        </Stack>
                      </CardContent>
                    </Card>
                  );
                })}
              </Stack>
            </Box>
          )}

          {analyses.length > 0 && <Divider />}

          {/* New Analysis Button */}
          <Box>
            <Button
              variant="contained"
              startIcon={<PlaylistAddIcon />}
              onClick={() => setCreateDialogOpen(true)}
              disabled={runningAnalysis || checklistsLoading}
              fullWidth={analyses.length === 0}
            >
              Նոր վերլուծություն
            </Button>
            {checklists.length === 0 && !checklistsLoading && (
              <Alert severity="info" sx={{ mt: 2 }}>
                Դուք դեռ ստուգացուցակներ չունեք։ Ստեղծեք մեկը{' '}
                <Button
                  size="small"
                  onClick={() => window.location.href = '/ai-analysis'}
                  sx={{ textTransform: 'none' }}
                >
                  այստեղ
                </Button>
              </Alert>
            )}
          </Box>
        </Stack>
      </CardContent>

      {/* Details Dialog */}
      <Dialog
        open={detailsDialogOpen}
        onClose={() => {
          setDetailsDialogOpen(false);
          setContextExpanded(false);
          setIsEditingComment(false);
        }}
        disableScrollLock
        maxWidth="md"
        fullWidth
        PaperProps={{ sx: { borderRadius: 2 } }}
      >
        <DialogTitle sx={{ pb: 1.5, pt: 2 }}>
          <Stack direction="row" justifyContent="space-between" alignItems="center">
            <Box sx={{ flex: 1, minWidth: 0 }}>
              <Typography variant="h6" sx={{ fontWeight: 600, mb: 0.5 }}>
                {selectedAnalysis?.checklistSnapshot?.name || 'Վերլուծություն'}
              </Typography>
              <Typography variant="caption" color="text.secondary">
                {formatDate(selectedAnalysis?.completedAt || selectedAnalysis?.createdAt)}
              </Typography>
            </Box>
            <IconButton onClick={() => {
              setDetailsDialogOpen(false);
              setContextExpanded(false);
              setIsEditingComment(false);
            }} size="small" sx={{ ml: 1 }}>
              <CloseIcon fontSize="small" />
            </IconButton>
          </Stack>
        </DialogTitle>
        <DialogContent sx={{ pt: 2, pb: 1 }}>
          {selectedAnalysis && (
            <Box>
              {selectedAnalysis.checklistSnapshot?.description && (
                <Typography variant="body2" color="text.secondary" sx={{ mb: 2, lineHeight: 1.5 }}>
                  {selectedAnalysis.checklistSnapshot.description}
                </Typography>
              )}

              {selectedAnalysis.checklistSnapshot?.context && (
                <Box sx={{ mb: 2 }}>
                  <Button
                    onClick={() => setContextExpanded(!contextExpanded)}
                    size="small"
                    endIcon={contextExpanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                    sx={{
                      textTransform: 'none',
                      color: 'text.secondary',
                      fontWeight: 600,
                      p: 0.5,
                      minWidth: 'auto',
                      '&:hover': {
                        bgcolor: 'transparent'
                      }
                    }}
                  >
                    <Typography variant="caption" sx={{ fontWeight: 600 }}>
                      Կոնտեքստ
                    </Typography>
                  </Button>
                  <Collapse in={contextExpanded}>
                    <Typography
                      variant="caption"
                      sx={{
                        whiteSpace: 'pre-wrap',
                        lineHeight: 1.5,
                        color: 'text.secondary',
                        display: 'block',
                        mt: 1,
                        pl: 1
                      }}
                    >
                      {selectedAnalysis.checklistSnapshot.context}
                    </Typography>
                  </Collapse>
                </Box>
              )}

              {/* Comment Section */}
              <Box sx={{ mb: 2 }}>
                <Typography variant="caption" sx={{ fontWeight: 600, color: 'text.secondary', mb: 1, display: 'block' }}>
                  Մեկնաբանություն
                </Typography>

                {isEditingComment ? (
                  <Stack spacing={1}>
                    <TextField
                      multiline
                      rows={3}
                      value={comment}
                      onChange={(e) => setComment(e.target.value)}
                      placeholder="Գրեք ձեր մեկնաբանությունը..."
                      size="small"
                      fullWidth
                      variant="outlined"
                      autoFocus
                    />
                    <Stack direction="row" spacing={1} justifyContent="flex-end">
                      <Button
                        size="small"
                        onClick={() => {
                          setIsEditingComment(false);
                          setComment(selectedAnalysis.comment || '');
                        }}
                      >
                        Չեղարկել
                      </Button>
                      <Button
                        size="small"
                        variant="contained"
                        startIcon={savingComment ? <CircularProgress size={16} /> : <SaveIcon />}
                        onClick={async () => {
                          if (!selectedAnalysis?.id) return;
                          setSavingComment(true);
                          try {
                            const newComment = comment.trim() || null;
                            await updateDoc(doc(db, 'analyses', selectedAnalysis.id), {
                              comment: newComment,
                              commentUpdatedAt: serverTimestamp()
                            });
                            // Immediate local update for UX responsiveness
                            setSelectedAnalysis(prev => ({ ...prev, comment: newComment }));
                            setIsEditingComment(false);
                            amplitude.track('Analysis Comment Saved', { analysisId: selectedAnalysis.id });
                          } catch (error) {
                            console.error('Error saving comment:', error);
                            alert('Սխալ՝ մեկնաբանությունը պահպանելիս');
                          } finally {
                            setSavingComment(false);
                          }
                        }}
                        disabled={savingComment}
                      >
                        Պահպանել
                      </Button>
                    </Stack>
                  </Stack>
                ) : (
                  <Box>
                    {!selectedAnalysis.comment ? (
                      <Button
                        variant="outlined"
                        startIcon={<NoteAddIcon />}
                        size="small"
                        fullWidth
                        onClick={() => {
                          setComment('');
                          setIsEditingComment(true);
                        }}
                        sx={{
                          borderStyle: 'dashed',
                          color: 'text.secondary',
                          py: 1.5,
                          '&:hover': {
                            borderStyle: 'dashed',
                            bgcolor: 'action.hover'
                          }
                        }}
                      >
                        Ավելացնել մեկնաբանություն
                      </Button>
                    ) : (
                      <Paper
                        elevation={0}
                        onClick={() => {
                          setComment(selectedAnalysis.comment || '');
                          setIsEditingComment(true);
                        }}
                        sx={{
                          p: 1.5,
                          bgcolor: 'grey.50',
                          borderRadius: 1,
                          cursor: 'pointer',
                          position: 'relative',
                          border: '1px solid transparent',
                          transition: 'all 0.2s',
                          '&:hover': {
                            bgcolor: 'action.hover',
                            borderColor: 'divider',
                            '& .edit-icon-overlay': {
                              opacity: 1
                            }
                          }
                        }}
                      >
                        <Stack direction="row" alignItems="flex-start" justifyContent="space-between">
                          <Typography
                            variant="body2"
                            sx={{
                              whiteSpace: 'pre-wrap',
                              color: 'text.primary',
                              flex: 1
                            }}
                          >
                            {selectedAnalysis.comment}
                          </Typography>
                          <EditIcon
                            className="edit-icon-overlay"
                            fontSize="small"
                            sx={{
                              opacity: 0,
                              color: 'text.secondary',
                              transition: 'opacity 0.2s',
                              ml: 1
                            }}
                          />
                        </Stack>
                      </Paper>
                    )}
                  </Box>
                )}
              </Box>

              {renderAnalysisResults(selectedAnalysis)}
            </Box>
          )}
        </DialogContent>
        <DialogActions sx={{ p: 1.5, pt: 1 }}>
          <Button onClick={() => {
            setDetailsDialogOpen(false);
            setContextExpanded(false);
            setIsEditingComment(false);
          }} size="small">Փակել</Button>
        </DialogActions>
      </Dialog>


      {/* Create Analysis Dialog */}
      <Dialog
        open={createDialogOpen}
        onClose={() => setCreateDialogOpen(false)}
        disableScrollLock
        maxWidth="sm"
        fullWidth
        PaperProps={{ sx: { borderRadius: 2 } }}
      >
        <DialogTitle sx={{ fontWeight: 600 }}>
          Սկսել նոր վերլուծություն
        </DialogTitle>
        <DialogContent sx={{ pt: 2 }}>
          <Stack spacing={2} sx={{ mt: 1 }}>
            <Typography variant="body2" color="text.secondary">
              Ընտրեք ստուգացուցակ, որով ցանկանում եք վերլուծել այս ձայնագրությունը։
            </Typography>
            <FormControl fullWidth size="small">
              <InputLabel>Ընտրել ստուգացուցակ</InputLabel>
              <Select
                value={selectedChecklistId}
                label="Ընտրել ստուգացուցակ"
                onChange={(e) => setSelectedChecklistId(e.target.value)}
                disabled={checklistsLoading || runningAnalysis}
              >
                {checklists.map((checklist) => (
                  <MenuItem key={checklist.id} value={checklist.id}>
                    {checklist.name} ({checklist.questions?.length || 0} հարց)
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Stack>
        </DialogContent>
        <DialogActions sx={{ p: 2 }}>
          <Button
            onClick={() => setCreateDialogOpen(false)}
            color="inherit"
          >
            Չեղարկել
          </Button>
          <Button
            variant="contained"
            startIcon={runningAnalysis ? <CircularProgress size={16} color="inherit" /> : <PlayIcon />}
            onClick={handleRunAnalysis}
            disabled={!selectedChecklistId || checklistsLoading || runningAnalysis}
          >
            {runningAnalysis ? 'Սկսվում է...' : 'Սկսել'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog
        open={deleteDialogOpen}
        onClose={() => setDeleteDialogOpen(false)}
        disableScrollLock
        maxWidth="xs"
        fullWidth
        PaperProps={{ sx: { borderRadius: 2 } }}
      >
        <DialogTitle sx={{ fontWeight: 600, color: 'error.main' }}>
          Ջնջե՞լ վերլուծությունը
        </DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary">
            Դուք համոզվա՞ծ եք, որ ցանկանում եք ջնջել այս վերլուծությունը։ Այս գործողությունը անդառնալի է։
          </Typography>
        </DialogContent>
        <DialogActions sx={{ p: 2 }}>
          <Button
            onClick={() => setDeleteDialogOpen(false)}
            color="inherit"
            disabled={deleting}
          >
            Չեղարկել
          </Button>
          <Button
            variant="contained"
            color="error"
            startIcon={deleting ? <CircularProgress size={16} color="inherit" /> : <DeleteIcon />}
            onClick={confirmDelete}
            disabled={deleting}
          >
            {deleting ? 'Ջնջվում է...' : 'Ջնջել'}
          </Button>
        </DialogActions>
      </Dialog>
    </Card >
  );
};

export default ChecklistAnalysisSection;
